import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface Partner {
  id: string;
  name: { ar: string; en: string };
  logo: string;
  order: number;
}

export function PartnersSection() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPartner, setCurrentPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState({
    name: { ar: '', en: '' },
    logo: '',
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const partnersQuery = query(
        collection(db, 'landing_partners'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(partnersQuery);

      const partnersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partner[];

      setPartners(partnersData);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (partner?: Partner) => {
    if (partner) {
      setCurrentPartner(partner);
      setFormData({
        name: typeof partner.name === 'string' ? { ar: partner.name, en: '' } : partner.name,
        logo: partner.logo,
      });
    } else {
      setCurrentPartner(null);
      setFormData({
        name: { ar: '', en: '' },
        logo: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPartner(null);
    setFormData({
      name: { ar: '', en: '' },
      logo: '',
    });
  };

  const handleSave = async () => {
    try {
      if (currentPartner) {
        // Update existing partner
        const partnerRef = doc(db, 'landing_partners', currentPartner.id);
        await updateDoc(partnerRef, {
          name: formData.name,
          logo: formData.logo,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Add new partner
        await addDoc(collection(db, 'landing_partners'), {
          name: formData.name,
          logo: formData.logo,
          order: partners.length,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
      await fetchPartners();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving partner:', err);
    }
  };

  const handleDelete = async (partnerId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الشريك؟')) return;

    try {
      await deleteDoc(doc(db, 'landing_partners', partnerId));
      await fetchPartners();
    } catch (err) {
      console.error('Error deleting partner:', err);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newPartners = [...partners];
    [newPartners[index - 1], newPartners[index]] = [newPartners[index], newPartners[index - 1]];

    await updateOrderInFirebase(newPartners);
  };

  const handleMoveDown = async (index: number) => {
    if (index === partners.length - 1) return;

    const newPartners = [...partners];
    [newPartners[index], newPartners[index + 1]] = [newPartners[index + 1], newPartners[index]];

    await updateOrderInFirebase(newPartners);
  };

  const updateOrderInFirebase = async (orderedPartners: Partner[]) => {
    try {
      const updatePromises = orderedPartners.map((partner, index) =>
        updateDoc(doc(db, 'landing_partners', partner.id), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );
      await Promise.all(updatePromises);
      await fetchPartners();
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  if (loading) {
    return <Card className="p-6">جاري التحميل...</Card>;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة الشركاء</h3>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 ml-2" />
          إضافة شريك
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {partners.map((partner, index) => (
          <Card key={partner.id} className="p-4">
            <div className="aspect-video mb-4 rounded-lg overflow-hidden bg-gray-100">
              <img
                src={partner.logo}
                alt={typeof partner.name === 'string' ? partner.name : partner.name.ar}
                className="w-full h-full object-contain"
              />
            </div>
            <h4 className="font-semibold mb-2 text-center">{typeof partner.name === 'string' ? partner.name : partner.name.ar}</h4>
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
              >
                ↑
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMoveDown(index)}
                disabled={index === partners.length - 1}
              >
                ↓
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleOpenModal(partner)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(partner.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {currentPartner ? 'تعديل شريك' : 'إضافة شريك جديد'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">اسم الشريك / Partner Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="name-ar"
                    value={formData.name.ar}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                    placeholder="مثال: وزارة الصحة"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="name-en"
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                    placeholder="e.g. Ministry of Health"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="logo">رابط الشعار</Label>
              <Input
                id="logo"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            {formData.logo && (
              <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={formData.logo}
                  alt="معاينة الشعار"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              إلغاء
            </Button>
            <Button onClick={handleSave}>
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

